import { useMemo } from "react";
import { useNavigate } from "react-router";

import type { ProjectConnectionDetailsDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { PlusIcon } from "@/components/Icons/PlusIcon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PillList } from "@/components/Pill/PillList";
import { Button } from "@/design-system/Button";
import { createDashboardColumnHelper } from "@/design-system/Table/helpers";
import { TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface ProjectConnectionsListProps {
  connections: ProjectConnectionDetailsDto[];
  isLoading: boolean;
  onDelete: (connection: ProjectConnectionDetailsDto) => Promise<unknown>;
}

export function ProjectConnectionsList({
  connections,
  isLoading,
  onDelete,
}: ProjectConnectionsListProps): React.ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal<ProjectConnectionDetailsDto>(
    "project-connection-delete-modal",
  );

  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<ProjectConnectionDetailsDto>();

    return [
      helper.accessor("name", {
        header: t("page.project-connections.list.name"),
        cell: (cell) => cell.getValue(),
        meta: {
          shouldSimulateRowClick: true,
          renderTruncatedTooltip: (cell) => cell.getValue(),
        },
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("projects", {
        header: t("page.project-connections.list.projects"),
        cell: (cell) => {
          const value = cell.getValue();

          return (
            <PillList
              items={value.map((project) => ({
                label: project.name,
              }))}
              maxShown={3}
            />
          );
        },
        minSize: 240,
        size: 540,
      }),
      helper.accessor("groups", {
        header: t("page.project-connections.list.groups"),
        cell: (cell) => {
          const value = cell.getValue();
          const groups = value?.items || [];

          if (groups.length === 0) {
            return null;
          }

          return (
            <PillList
              maxShown={3}
              items={groups.map((group) => ({
                label: group.name,
                onClick: () =>
                  void navigate(routes.projectConnections.editGroup({ id: cell.row.original.id, gid: group.id })),
              }))}
              onClickOmittedItems={() =>
                void navigate(routes.projectConnections.listGroups({ id: cell.row.original.id }))
              }
            />
          );
        },
        minSize: 240,
        size: 540,
      }),
      helper.accessor("internalDescription", {
        header: t("page.project-connections.list.description"),
        cell: (cell) => cell.getValue() || "",
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("id", {
        header: "",
        cell: (cell) => {
          const menuActions: ContextMenuItem[] = [
            {
              labels: {
                default: t("common.action.edit"),
              },
              value: "edit",
              icon: <Edit05Icon />,
              callbackFn: () => void navigate(routes.projectConnections.edit({ id: cell.row.original.id })),
            },
            {
              labels: {
                default: t("common.action.delete"),
              },
              value: "delete",
              icon: <Trash02Icon />,
              callbackFn: () => openDeleteModal(cell.row.original),
            },
            {
              labels: {
                default: t("page.project-connections.actions.create-group"),
              },
              value: "create-group",
              icon: <PlusIcon />,
              callbackFn: () => void navigate(routes.projectConnections.createGroup({ id: cell.row.original.id })),
            },
          ];

          return <ContextMenu items={menuActions} />;
        },
        size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
        enableResizing: false,
        meta: {
          isAction: true,
        },
      }),
    ];
  }, [navigate, openDeleteModal, t]);

  const tableInstance = useResizableTable(TABLE_KEYS.PROJECT_CONNECTIONS, {
    columns,
    data: connections,
    enableSorting: false,
  });

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.project-connections.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.portfolio.title"),
            },
            {
              name: t("page.project-connections.title"),
            },
          ]}
        />
      }
      actions={
        <Button type="link" data-testid="project-connection-action-create" href={routes.projectConnections.create()}>
          {t("page.project-connections.actions.create")}
        </Button>
      }
    >
      {connections.length === 0 && !isLoading && (
        <section className="px-space-sm">
          <p>{t("page.project-connections.list.empty")}</p>
        </section>
      )}
      {(connections.length > 0 || isLoading) && (
        <Table
          table={tableInstance}
          getRowHref={(row) => routes.projectConnections.edit({ id: row.original.id })}
          isLoading={isLoading}
        />
      )}
      <DeleteModal
        title={t("page.project-connections.actions.delete.title")}
        description={t("page.project-connections.actions.delete.description")}
        onDelete={onDelete}
        deleteBtnProps={{
          "data-testid": "project-connection-action-delete",
        }}
        {...deleteModalProps}
      />
    </DocumentPage>
  );
}
